"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import type { NavNode } from "@/lib/dto";
import { ChevronDown } from "lucide-react";

interface MainNavProps {
  menuData?: NavNode[] | null;
}

/**
 * Matches production's real header nav (confirmed via the live DOM/hover
 * state and the /api/public/navigation payload): each top-level item with
 * children opens a full-width mega panel — a left-side vertical tab list
 * with a thin divider, and the active tab's children on the right.
 *
 * The panel is click-to-open (not hover) and closes on outside click or
 * when any link inside it is clicked, so it doesn't linger over the page
 * after navigation.
 *
 * The right side has two shapes depending on data depth, and both occur
 * in the live payload under the same "Destinations" menu (Nepal vs.
 * Tibet/Bhutan/India), so the panel must detect it rather than assume one:
 *  - flat: the tab's children are leaf links (Packages, Activities, and
 *    most Destinations tabs) → a single-column link list.
 *  - grouped: the tab's children are category headings that themselves
 *    have children (Destinations → Nepal: Trekking, Peak Climbing, ...)
 *    → a masonry-packed grid of "heading + link list" groups.
 */
export default function MainNav({ menuData }: MainNavProps) {
  const menus = menuData ?? [];
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    if (openId === null) return;

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-mega-nav-item]")) {
        setOpenId(null);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [openId]);

  return (
    <>
      {menus.map((item) => (
        <li key={item.id} data-mega-nav-item className="group relative">
          {item.children?.length ? (
            <button
              type="button"
              className="nav-link relative flex h-[5.25rem] items-center px-3 font-secondary text-[15px] leading-none font-bold text-headings data-[state=open]:text-primary"
              data-state={openId === item.id ? "open" : "closed"}
              onClick={() =>
                setOpenId((current) => (current === item.id ? null : item.id))
              }
            >
              {item.displayLabel ?? item.label}
              <ChevronDown
                size={14}
                strokeWidth={2}
                aria-hidden
                className={`relative top-px ml-0.5 opacity-80 transition-transform duration-300 ${openId === item.id ? "rotate-180" : ""}`}
              />
            </button>
          ) : (
            <Link
              href={item.href}
              className="nav-link relative flex h-[5.25rem] items-center px-3 font-secondary text-[15px] leading-none font-bold text-headings"
            >
              {item.displayLabel ?? item.label}
            </Link>
          )}

          {item.children?.length ? (
            <MegaPanel
              item={item}
              open={openId === item.id}
              onNavigate={() => setOpenId(null)}
            />
          ) : null}
        </li>
      ))}
    </>
  );
}

function MegaPanel({
  item,
  open,
  onNavigate,
}: {
  item: NavNode;
  open: boolean;
  onNavigate: () => void;
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const tabs = item.children ?? [];
  const active = tabs[activeIdx] ?? tabs[0];
  const children = active?.children ?? [];
  const isGrouped = children.some((child) => child.children?.length);

  if (!open) return null;

  return (
    <div className="absolute top-full left-0 w-[1240px] max-w-[calc(100vw-2rem)] border border-line bg-paper shadow-2xl">
      <div className="grid grid-cols-5 gap-x-6 p-6">
        <div className="relative flex flex-col items-start before:absolute before:-inset-y-6 before:right-0 before:w-px before:bg-line">
          <div className="max-h-[440px] w-full space-y-1 overflow-y-auto pr-4">
            {tabs.map((tab, idx) => (
              <button
                key={tab.id}
                type="button"
                onMouseEnter={() => setActiveIdx(idx)}
                className={`w-full relative py-1.5 pr-6 pl-0 text-left text-[15px] font-semibold after:absolute after:inset-y-0  after:bg-primary after:-right-4  ${
                  idx === activeIdx
                    ? "text-primary after:w-0.5"
                    : "text-headings after:w-0"
                }`}
              >
                {tab.displayLabel ?? tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="col-span-4 max-h-[440px] overflow-y-auto py-0">
          {isGrouped ? (
            <div className="columns-2 gap-x-10">
              {children.map((group) => (
                <div key={group.id} className="mb-6 break-inside-avoid">
                  <p className="mb-2 text-sm font-bold tracking-wide text-headings uppercase">
                    {group.displayLabel ?? group.label}
                  </p>
                  <ul className="space-y-2.5">
                    {(group.children ?? []).map((link) => (
                      <li key={link.id}>
                        <Link
                          href={link.href}
                          onClick={onNavigate}
                          className="block text-sm text-text hover:text-primary hover:underline"
                        >
                          {link.displayLabel ?? link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <ul className="child-menu grid grid-cols-2 gap-x-6">
              {children.map((link) => (
                <li key={link.id}>
                  <Link
                    href={link.href}
                    onClick={onNavigate}
                    className="block py-1.5 text-sm text-headings hover:text-primary hover:underline"
                  >
                    {link.displayLabel ?? link.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
